import React, { useId } from "react";
import styles from "./admin.module.scss";
import cx from "./cx";
import Icon from "./Icon";

interface FieldShellProps {
  id: string;
  label?: React.ReactNode;
  required?: boolean;
  help?: React.ReactNode;
  error?: React.ReactNode;
  counter?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}

/** Label + control + help/error text, laid out as in the design. */
export const FieldShell: React.FC<FieldShellProps> = ({
  id,
  label,
  required,
  help,
  error,
  counter,
  className,
  children,
}) => (
  <div className={cx(styles.fld, className)}>
    {label && (
      <label className={styles.lbl} htmlFor={id}>
        {label}
        {required && <span className={styles.req}> *</span>}
      </label>
    )}
    {children}
    {(error || help || counter) && (
      <div className={styles.helpRow}>
        {error ? (
          <span className={styles.errt} id={`${id}-err`} role="alert">
            {error}
          </span>
        ) : help ? (
          <span className={styles.help} id={`${id}-help`}>
            {help}
          </span>
        ) : (
          <span />
        )}
        {counter}
      </div>
    )}
  </div>
);

const describedBy = (id: string, error?: React.ReactNode, help?: unknown) =>
  error ? `${id}-err` : help ? `${id}-help` : undefined;

interface CommonFieldProps {
  label?: React.ReactNode;
  help?: React.ReactNode;
  error?: React.ReactNode;
  fieldClassName?: string;
}

export type TextFieldProps = CommonFieldProps &
  React.InputHTMLAttributes<HTMLInputElement>;

export const TextField = React.forwardRef<HTMLInputElement, TextFieldProps>(
  (
    { label, help, error, fieldClassName, className, id, required, ...rest },
    ref,
  ) => {
    const autoId = useId();
    const fid = id ?? autoId;
    return (
      <FieldShell
        id={fid}
        label={label}
        required={required}
        help={help}
        error={error}
        className={fieldClassName}
      >
        <input
          ref={ref}
          id={fid}
          required={required}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy(fid, error, help)}
          className={cx(styles.inp, !!error && styles.inpErr, className)}
          {...rest}
        />
      </FieldShell>
    );
  },
);
TextField.displayName = "TextField";

export type SelectProps = CommonFieldProps &
  React.SelectHTMLAttributes<HTMLSelectElement> & {
    options?: Array<{ value: string | number; label: React.ReactNode }>;
  };

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      label,
      help,
      error,
      fieldClassName,
      className,
      id,
      required,
      options,
      children,
      ...rest
    },
    ref,
  ) => {
    const autoId = useId();
    const fid = id ?? autoId;
    return (
      <FieldShell
        id={fid}
        label={label}
        required={required}
        help={help}
        error={error}
        className={fieldClassName}
      >
        <select
          ref={ref}
          id={fid}
          required={required}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy(fid, error, help)}
          className={cx(
            styles.inp,
            styles.select,
            !!error && styles.inpErr,
            className,
          )}
          {...rest}
        >
          {options
            ? options.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))
            : children}
        </select>
      </FieldShell>
    );
  },
);
Select.displayName = "Select";

export type TextareaProps = CommonFieldProps &
  React.TextareaHTMLAttributes<HTMLTextAreaElement> & {
    /** Shows «n / max» under the field. Does not cut the text. */
    maxChars?: number;
    /** Over `maxChars` is allowed: the counter turns warning-coloured instead of error-red. */
    softLimit?: boolean;
  };

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      label,
      help,
      error,
      fieldClassName,
      className,
      id,
      required,
      maxChars,
      softLimit,
      value,
      defaultValue,
      ...rest
    },
    ref,
  ) => {
    const autoId = useId();
    const fid = id ?? autoId;
    const length = String(value ?? defaultValue ?? "").length;
    const counter =
      maxChars !== undefined ? (
        <span
          className={cx(
            styles.cnt,
            length > maxChars && (softLimit ? styles.cntWarn : styles.cntOver),
          )}
          aria-live="polite"
        >
          {length} / {maxChars}
        </span>
      ) : undefined;
    return (
      <FieldShell
        id={fid}
        label={label}
        required={required}
        help={help}
        error={error}
        counter={counter}
        className={fieldClassName}
      >
        <textarea
          ref={ref}
          id={fid}
          required={required}
          value={value}
          defaultValue={defaultValue}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy(fid, error, help)}
          className={cx(
            styles.inp,
            styles.ta,
            !!error && styles.inpErr,
            className,
          )}
          {...rest}
        />
      </FieldShell>
    );
  },
);
Textarea.displayName = "Textarea";

type ChoiceProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, "type"> & {
  label?: React.ReactNode;
};

const Choice = (
  kind: "checkbox" | "radio" | "switch",
  { label, className, disabled, ...rest }: ChoiceProps,
  ref: React.ForwardedRef<HTMLInputElement>,
) => (
  <label
    className={cx(styles.choice, disabled && styles.choiceDisabled, className)}
  >
    <input
      ref={ref}
      type={kind === "radio" ? "radio" : "checkbox"}
      role={kind === "switch" ? "switch" : undefined}
      className={styles.nativeHidden}
      disabled={disabled}
      {...rest}
    />
    {kind === "switch" && <span className={styles.sw} aria-hidden="true" />}
    {kind === "checkbox" && (
      <span className={styles.cb} aria-hidden="true">
        <Icon name="check" size={12} strokeWidth={3} />
      </span>
    )}
    {kind === "radio" && <span className={styles.rd} aria-hidden="true" />}
    {label}
  </label>
);

export const Toggle = React.forwardRef<HTMLInputElement, ChoiceProps>(
  (props, ref) => Choice("switch", props, ref),
);
Toggle.displayName = "Toggle";

export const Checkbox = React.forwardRef<HTMLInputElement, ChoiceProps>(
  (props, ref) => Choice("checkbox", props, ref),
);
Checkbox.displayName = "Checkbox";

export const Radio = React.forwardRef<HTMLInputElement, ChoiceProps>(
  (props, ref) => Choice("radio", props, ref),
);
Radio.displayName = "Radio";

export interface SegmentedControlProps<T extends string | number> {
  options: Array<{ value: T; label: React.ReactNode }>;
  value: T;
  onChange: (value: T) => void;
  /** Accessible name for the group. */
  label: string;
  className?: string;
}

export function SegmentedControl<T extends string | number>({
  options,
  value,
  onChange,
  label,
  className,
}: SegmentedControlProps<T>) {
  return (
    <div
      className={cx(styles.seg, className)}
      role="radiogroup"
      aria-label={label}
    >
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={String(o.value)}
            type="button"
            role="radio"
            aria-checked={on}
            className={cx(styles.segItem, on && styles.segOn)}
            onClick={() => onChange(o.value)}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
